/**
 * GetUserContextQuery - Fetches current user context from API
 * Handles snake_case to camelCase transformation
 */

import type { IQuery, IQueryHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import type { UserContextData } from './userContext.types';
import { toCamelCase } from '@/utils/caseTransform';

// ============================================================================
// Query (Request)
// ============================================================================

export class GetUserContextQuery implements IQuery<UserContextData> {
  readonly type = 'GetUserContextQuery' as const;
}

// ============================================================================
// Query Handler
// ============================================================================

export class GetUserContextQueryHandler implements IQueryHandler<GetUserContextQuery, UserContextData> {
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(_query: GetUserContextQuery): Promise<UserContextData> {
    const response = await this.httpClient.get<BaseApiResponse<any>>('/api/v1/user/current');

    if (!response.succeeded) {
      throw new Error(response.message || 'Failed to fetch user context');
    }

    // Transform snake_case to camelCase (shallow + nested objects)
    const data = toCamelCase(response.data);
    if (data.tenant) {
      data.tenant = toCamelCase(data.tenant);
    }
    if (data.billing) {
      data.billing = toCamelCase(data.billing);
    }

    return data as UserContextData;
  }
}
